/**
 * Indian Rummy bot.
 *
 * - Draw: takes the top discard when it is a wild joker or clearly brings the hand closer to a winning arrangement,
 *   otherwise draws from the stock.
 * - Discard: declares as soon as some discard leaves a complete hand; otherwise throws the card whose removal leaves the
 *   hand closest to winning, preferring cards that are not building towards a sequence or set.
 * - Drop: NORMAL and above leave on the first turn when the hand has no wild joker and is hopelessly scattered.
 * The bot only ever reads its own seat's view.
 */
import { parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import type { RummyAction, RummyLegal, RummyView } from './engine.js';
import { analyzeRummyHand, isRummyWild, rummyCardPoints } from './melds.js';

/** How many other cards in the hand could join this one in a sequence or set. */
function synergy(hand: readonly CardId[], i: number, wildRank: number): number {
  const c = parseCard(hand[i]!);
  let s = 0;
  hand.forEach((h, j) => {
    if (i === j || isRummyWild(h, wildRank)) return;
    const o = parseCard(h);
    if (o.suit === c.suit) {
      const gap = Math.min(Math.abs(o.rank - c.rank), c.rank === 14 || o.rank === 14 ? Math.abs((o.rank === 14 ? 1 : o.rank) - (c.rank === 14 ? 1 : c.rank)) : 99);
      if (gap === 1) s += 3;
      else if (gap === 2) s += 2;
    } else if (o.rank === c.rank) s += 1.5;
  });
  return s;
}

/** Score of discarding card `i` from a 14-card hand: lower is better. */
function discardScore(hand: readonly CardId[], i: number, wildRank: number, soft: number): number {
  const wild = isRummyWild(hand[i]!, wildRank);
  return soft + (wild ? 60 : synergy(hand, i, wildRank) * 1.5);
}

export function rummyChooseDiscard(hand: readonly CardId[], wildRank: number): { index: number; made: boolean } {
  const an = analyzeRummyHand(hand, wildRank);
  let best = -1;
  let bestScore = Infinity;
  for (let i = 0; i < hand.length; i++) {
    if (an.isMade(i)) return { index: i, made: true };
    const score = discardScore(hand, i, wildRank, an.softScore(i)) - rummyCardPoints(hand[i]!, wildRank) * 0.01;
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return { index: best, made: false };
}

function shouldDrop(view: RummyView, ctx: BotContext): boolean {
  if (ctx.difficulty === 'BEGINNER' || ctx.difficulty === 'EASY') return false;
  const wildRank = view.wildRank;
  if (view.hand.some((c) => isRummyWild(c, wildRank))) return false;
  const an = analyzeRummyHand(view.hand, wildRank);
  const soft = an.softScore();
  const link = view.hand.reduce((n, _, i) => n + synergy(view.hand, i, wildRank), 0);
  return soft >= 120 && link < 32;
}

export const rummyBot: BotEngine<RummyView, RummyLegal, RummyAction> = {
  decideAction(view, legal, ctx) {
    if (legal.canDrawStock || legal.canDrawDiscard || legal.canDrop) {
      if (legal.canDrop && legal.dropPoints <= view.config.firstDrop && shouldDrop(view, ctx)) return { type: 'drop' };
      const top = view.discardTop;
      if (legal.canDrawDiscard && top && ctx.difficulty !== 'BEGINNER' && !chance(ctx, ctx.profile.mistakeRate)) {
        if (isRummyWild(top, view.wildRank)) return { type: 'draw', from: 'discard' };
        const current = analyzeRummyHand(view.hand, view.wildRank).softScore();
        const withTop = [...view.hand, top];
        const an = analyzeRummyHand(withTop, view.wildRank);
        let best = Infinity;
        for (let i = 0; i < view.hand.length; i++) best = Math.min(best, an.softScore(i));
        if (best < current - 4) return { type: 'draw', from: 'discard' };
      }
      if (legal.canDrawStock) return { type: 'draw', from: 'stock' };
      return { type: 'draw', from: 'discard' };
    }
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (!legal.canDiscard) return this.handleTimeout(view, legal, ctx);
    const hand = view.hand;
    const choice = rummyChooseDiscard(hand, view.wildRank);
    if (choice.made && legal.canFinish) return { type: 'finish', discard: hand[choice.index]! };
    if (chance(ctx, ctx.profile.mistakeRate)) {
      const pool = hand.filter((c) => !isRummyWild(c, view.wildRank));
      if (pool.length > 0) return { type: 'discard', card: pool[ctx.rng.int(pool.length)]! };
    }
    return { type: 'discard', card: hand[choice.index]! };
  },

  evaluateState(view) {
    if (view.seat === null || view.hand.length === 0) return 0;
    const soft = analyzeRummyHand(view.hand.slice(0, 13), view.wildRank).softScore();
    return Math.max(0, Math.min(1, 1 - soft / 120));
  },

  calculateRisk(view, legal) {
    return legal.canDrop ? Math.min(1, view.totals[view.seat ?? 0]! / 160) : 0;
  },

  handleTimeout(view, legal) {
    if (legal.canDrawStock) return { type: 'draw', from: 'stock' };
    if (legal.canDrawDiscard) return { type: 'draw', from: 'discard' };
    const best = [...view.hand].sort((a, b) => rummyCardPoints(b, view.wildRank) - rummyCardPoints(a, view.wildRank))[0]!;
    return { type: 'discard', card: best };
  },
};
